import { useState, useEffect, useCallback, useRef } from 'react';
import { Search, ChevronDown, Check, Loader2, X } from 'lucide-react';

// Mapování úrovně vzdělání → typ studia z API
// Mapování místo studia: API ID → display label
const MISTO_OPTIONS = [
    { id: 'CR', label: 'ČR' },
    { id: 'Z', label: 'Zahraničí' },
];

// Mapování úrovně vzdělání → typ studia z API
const VZDELANI_TO_TYP = {
    'Střední odborné (SOU)': ['SSB'],
    'Střední s maturitou':   ['SSS'],
    'Vyšší odborné (VOS)':   ['VOS', 'VOA', 'VOB'],
    'Vysokoškolské':         ['VSB', 'VSM', 'VSD'],
};

const inputStyle = {
    background: 'var(--surface-elevated)',
    border: '1px solid var(--border-default)',
    color: 'var(--text-primary)',
    borderRadius: '8px',
    padding: '8px 12px',
    width: '100%',
    outline: 'none',
    fontSize: '14px',
    colorScheme: 'inherit',
};

export default function KkovSelect({ vzdelani, value, onChange }) {
    const [initData, setInitData] = useState(null);
    const [loading, setLoading] = useState(true);

    // Stav výběru
    const [misto, setMisto] = useState('CR');
    const [rok, setRok] = useState('');
    const [typ, setTyp] = useState('');
    const [obec, setObec] = useState('');
    const [skola, setSkola] = useState('');
    const [obor, setObor] = useState('');

    // Seznamy z API
    const [typyStudia, setTypyStudia] = useState([]);
    const [obce, setObce] = useState([]);
    const [skoly, setSkoly] = useState([]);
    const [obory, setObory] = useState([]);
    const [vyslednyKod, setVyslednyKod] = useState(value || '');
    const [rezim, setRezim] = useState(null); // 1=BASO, 2=full

    const [updating, setUpdating] = useState(false);

    // Searchable dropdown state
    const [oborSearch, setOborSearch] = useState('');
    const [oborOpen, setOborOpen] = useState(false);
    const oborRef = useRef(null);

    // Načti init data
    useEffect(() => {
        fetch('/api/kkov/init')
            .then(r => r.json())
            .then(data => {
                setInitData(data);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, []);

    // Po načtení init dat automaticky inicializovat místo studia
    useEffect(() => {
        if (initData) {
            updateStav('misto', misto, { misto, rok: '', typ: '', obec: '', skola: '', obor: '' });
        }
    }, [initData]); // eslint-disable-line react-hooks/exhaustive-deps

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (oborRef.current && !oborRef.current.contains(e.target)) setOborOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Zavolej API pro aktualizaci stavu výběru
    const updateStav = useCallback(async (pole, hodnota, currentState = {}) => {
        setUpdating(true);
        const params = new URLSearchParams();
        if (currentState.misto || misto) params.set('misto', currentState.misto || misto);
        if (currentState.rok || rok) params.set('rok', currentState.rok || rok);
        if (currentState.typ || typ) params.set('typ', currentState.typ || typ);
        if (currentState.obec || obec) params.set('obec', currentState.obec || obec);
        if (currentState.skola || skola) params.set('skola', currentState.skola || skola);
        if (currentState.obor || obor) params.set('obor', currentState.obor || obor);
        params.set('pole', pole);
        params.set('hodnota', hodnota);

        try {
            const resp = await fetch(`/api/kkov/stav-vyberu?${params}`);
            const data = await resp.json();

            if (data.seznamTypuStudia) setTypyStudia(data.seznamTypuStudia);
            if (data.seznamObci) setObce(data.seznamObci);
            if (data.seznamSkol) setSkoly(data.seznamSkol);
            if (data.seznamOboru) setObory(data.seznamOboru);
            if (data.rezimVyberu != null) setRezim(data.rezimVyberu);

            if (data.vybranyObor?.id) {
                setObor(data.vybranyObor.id);
            }

            // Výsledný kód — fallback: pokud API nevrátí kód, použít obor ID
            const kod = data.vyslednyKod || '';
            const finalKod = kod || (currentState.obor && pole === 'obor' ? hodnota : '');
            setVyslednyKod(finalKod);
            if (finalKod) {
                const currentObor = data.vybranyObor?.id || currentState.obor || obor;
                const selectedObor = data.seznamOboru?.find(o => o.id === currentObor)
                    || data.vybranyObor
                    || obory.find(o => o.id === currentObor);
                onChange(finalKod, selectedObor?.nazev || '');
            }
        } catch (e) {
            console.warn('KKOV API error:', e);
        } finally {
            setUpdating(false);
        }
    }, [misto, rok, typ, obec, skola, obor, onChange]);

    // Předfiltrované typy studia dle úrovně vzdělání
    const allowedTypy = VZDELANI_TO_TYP[vzdelani] || [];
    const filteredTypy = typyStudia.length > 0
        ? typyStudia.filter(t => allowedTypy.length === 0 || allowedTypy.includes(t.id))
        : [];

    // Generuj roky (od initData)
    const roky = [];
    if (initData) {
        for (let y = initData.rokDo; y >= initData.rokOd; y--) {
            roky.push(y);
        }
    }

    // Handler pro místo
    const handleMisto = (v) => {
        setMisto(v);
        setRok(''); setTyp(''); setObec(''); setSkola(''); setObor('');
        setObory([]); setSkoly([]); setObce([]);
        setVyslednyKod('');
        updateStav('misto', v, { misto: v, rok: '', typ: '', obec: '', skola: '', obor: '' });
    };

    // Handler pro rok
    const handleRok = (v) => {
        setRok(v);
        setTyp(''); setObec(''); setSkola(''); setObor('');
        setObory([]); setSkoly([]); setObce([]);
        setVyslednyKod('');
        updateStav('rok', v, { misto, rok: v, typ: '', obec: '', skola: '', obor: '' });
    };

    // Handler pro typ studia
    const handleTyp = (v) => {
        setTyp(v);
        setObec(''); setSkola(''); setObor('');
        setObory([]); setSkoly([]);
        setVyslednyKod('');
        updateStav('typ', v, { misto, rok, typ: v, obec: '', skola: '', obor: '' });
    };

    // Handler pro obec
    const handleObec = (v) => {
        setObec(v);
        setSkola(''); setObor('');
        setObory([]); setSkoly([]);
        setVyslednyKod('');
        updateStav('obec', v, { misto, rok, typ, obec: v, skola: '', obor: '' });
    };

    // Handler pro školu
    const handleSkola = (v) => {
        setSkola(v);
        setObor('');
        setObory([]);
        setVyslednyKod('');
        updateStav('skola', v, { misto, rok, typ, obec, skola: v, obor: '' });
    };

    // Handler pro obor
    const handleObor = (v) => {
        setObor(v);
        setOborOpen(false);
        setOborSearch('');
        updateStav('obor', v, { misto, rok, typ, obec, skola, obor: v });
    };

    // Reset
    const handleReset = () => {
        setMisto('CR'); setRok(''); setTyp(''); setObec(''); setSkola(''); setObor('');
        setTypyStudia([]); setObce([]); setSkoly([]); setObory([]);
        setVyslednyKod(''); setRezim(null);
        onChange('', '');
    };

    // Filtrované obory dle hledání
    const filteredObory = oborSearch
        ? obory.filter(o => o.nazev.toLowerCase().includes(oborSearch.toLowerCase()) || o.id.toLowerCase().includes(oborSearch.toLowerCase()))
        : obory;

    if (loading) {
        return (
            <div className="flex items-center gap-2 py-3">
                <Loader2 size={14} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Načítám klasifikaci oborů…</span>
            </div>
        );
    }

    return (
        <div className="rounded-lg p-4 space-y-3" style={{ background: 'var(--surface-elevated)', border: '1px solid var(--border-default)' }}>
            <div className="flex items-center justify-between">
                <h4 className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                    Kód oboru vzdělání (KKOV)
                </h4>
                {updating && <Loader2 size={12} className="animate-spin" style={{ color: 'var(--brand)' }} />}
            </div>

            {/* Místo studia */}
            <div>
                <label className="text-[11px] font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Místo studia</label>
                <div className="flex gap-2">
                    {MISTO_OPTIONS.map(m => (
                        <button
                            key={m.id}
                            type="button"
                            onClick={() => handleMisto(m.id)}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium"
                            style={{
                                background: misto === m.id ? 'var(--brand-bg)' : 'var(--surface-raised)',
                                border: `1px solid ${misto === m.id ? 'var(--brand)' : 'var(--border-default)'}`,
                                color: misto === m.id ? 'var(--brand-text)' : 'var(--text-secondary)',
                                cursor: 'pointer',
                            }}
                        >
                            {m.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Rok ukončení */}
            <div>
                <label className="text-[11px] font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Rok ukončení studia</label>
                <select
                    value={rok}
                    onChange={e => handleRok(e.target.value)}
                    style={{ ...inputStyle, appearance: 'auto', fontSize: '13px' }}
                >
                    <option value="">Vyberte rok…</option>
                    {roky.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
            </div>

            {/* Typ školy */}
            {rok && filteredTypy.length > 0 && (
                <div>
                    <label className="text-[11px] font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Typ školy</label>
                    <select
                        value={typ}
                        onChange={e => handleTyp(e.target.value)}
                        style={{ ...inputStyle, appearance: 'auto', fontSize: '13px' }}
                    >
                        <option value="">Vyberte typ…</option>
                        {filteredTypy.map(t => <option key={t.id} value={t.id}>{t.nazev}</option>)}
                    </select>
                </div>
            )}

            {/* Obec (pokud režim 2 = full) */}
            {typ && rezim === 2 && obce.length > 0 && (
                <div>
                    <label className="text-[11px] font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Obec</label>
                    <select
                        value={obec}
                        onChange={e => handleObec(e.target.value)}
                        style={{ ...inputStyle, appearance: 'auto', fontSize: '13px' }}
                    >
                        <option value="">Vyberte obec…</option>
                        {obce.map(o => <option key={o.id} value={o.id}>{o.nazev}</option>)}
                    </select>
                </div>
            )}

            {/* Škola (pokud režim 2) */}
            {obec && rezim === 2 && skoly.length > 0 && (
                <div>
                    <label className="text-[11px] font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Škola</label>
                    <select
                        value={skola}
                        onChange={e => handleSkola(e.target.value)}
                        style={{ ...inputStyle, appearance: 'auto', fontSize: '13px' }}
                    >
                        <option value="">Vyberte školu…</option>
                        {skoly.map(s => <option key={s.id} value={s.id}>{s.nazev}</option>)}
                    </select>
                </div>
            )}

            {/* Obor – searchable dropdown */}
            {obory.length > 0 && (
                <div ref={oborRef} style={{ position: 'relative' }}>
                    <label className="text-[11px] font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Obor vzdělání</label>
                    <div
                        onClick={() => setOborOpen(!oborOpen)}
                        className="flex items-center gap-2"
                        style={{ ...inputStyle, cursor: 'pointer', fontSize: '13px' }}
                    >
                        <Search size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                        {oborOpen ? (
                            <input
                                autoFocus
                                value={oborSearch}
                                onChange={e => setOborSearch(e.target.value)}
                                onClick={e => e.stopPropagation()}
                                placeholder="Hledat obor…"
                                className="flex-1 bg-transparent outline-none text-sm"
                                style={{ color: 'var(--text-primary)', border: 'none', padding: 0 }}
                            />
                        ) : (
                            <span className="flex-1 text-sm truncate" style={{ color: obor ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                                {obor ? (obory.find(o => o.id === obor)?.nazev || obor) : 'Vyberte obor…'}
                            </span>
                        )}
                        <ChevronDown size={13} style={{ color: 'var(--text-muted)', flexShrink: 0, transform: oborOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
                    </div>

                    {oborOpen && (
                        <div
                            style={{
                                position: 'absolute', top: '100%', left: 0, right: 0,
                                background: 'var(--surface-raised)', border: '1px solid var(--border-default)',
                                borderRadius: '8px', marginTop: '4px', maxHeight: '200px', overflowY: 'auto',
                                zIndex: 50, boxShadow: 'var(--shadow-md)',
                            }}
                        >
                            {filteredObory.length === 0 ? (
                                <div className="px-3 py-2 text-xs" style={{ color: 'var(--text-muted)' }}>Žádné výsledky</div>
                            ) : (
                                filteredObory.map(o => (
                                    <button
                                        key={o.id}
                                        type="button"
                                        onClick={() => handleObor(o.id)}
                                        className="w-full text-left px-3 py-2 text-xs flex items-center justify-between"
                                        style={{
                                            color: obor === o.id ? 'var(--brand-text)' : 'var(--text-primary)',
                                            background: obor === o.id ? 'var(--brand-bg)' : 'transparent',
                                            border: 'none', cursor: 'pointer',
                                        }}
                                    >
                                        <span className="truncate">{o.nazev}</span>
                                        {obor === o.id && <Check size={12} style={{ color: 'var(--brand)', flexShrink: 0 }} />}
                                    </button>
                                ))
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Výsledný kód */}
            {vyslednyKod ? (
                <div className="flex items-center gap-2 pt-1">
                    <span className="text-[11px] font-medium" style={{ color: 'var(--text-muted)' }}>Výsledný kód:</span>
                    <span
                        className="text-xs font-mono font-semibold px-2 py-0.5 rounded"
                        style={{ background: 'rgba(16,185,129,0.12)', color: '#10B981' }}
                    >
                        {vyslednyKod}
                    </span>
                    <button type="button" onClick={handleReset} className="ml-auto" style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>
                        <X size={13} />
                    </button>
                </div>
            ) : (
                <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                    Postupně vyberte místo, rok, typ školy a obor pro vygenerování kódu.
                </p>
            )}
        </div>
    );
}
